import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActivityEntryQueryParams, ActivityEntryListItem } from "../types/activityEntry";
import { fetchChurchActivityEntries, fetchDistrictActivityEntries, fetchNationActivityEntries } from "../api/activityEntryApi";

export interface ActivityEntriesPagination {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
}

type ScopeType = 'CHURCH' | 'DISTRICT' | 'NATION';

interface UseActivityEntriesOptions {
    scope: ScopeType;
    scopeId: number;
    params: ActivityEntryQueryParams;
}

function buildListIdentity(scope: ScopeType, scopeId: number, activityEntryStatus: number | undefined): string {
    return `${scope}:${scopeId}:${activityEntryStatus ?? "all"}`;
}

export function useActivityEntries(churchId: number, params: ActivityEntryQueryParams) {
    return useActivityEntriesScoped({ scope: 'CHURCH', scopeId: churchId, params });
}

export function useActivityEntriesScoped({ scope, scopeId, params }: UseActivityEntriesOptions) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ActivityEntryListItem[]>([]);
    const [pagination, setPagination] = useState<ActivityEntriesPagination | null>(null);
    const requestParams = useMemo(
        () => ({
            activityEntryStatus: params.activityEntryStatus,
            departmentId: params.departmentId,
            reportTagId: params.reportTagId,
            reportTemplateId: params.reportTemplateId,
            page: params.page,
            perPage: params.perPage,
            freetext: params.freetext,
        }),
        [
            params.activityEntryStatus,
            params.departmentId,
            params.reportTagId,
            params.reportTemplateId,
            params.page,
            params.perPage,
            params.freetext,
        ]
    );
    const listIdentityRef = useRef(buildListIdentity(scope, scopeId, requestParams.activityEntryStatus));
    const requestIdRef = useRef(0);

    const enabled = scopeId > 0;

    const load = useCallback(async () => {
        const listIdentity = buildListIdentity(scope, scopeId, requestParams.activityEntryStatus);
        const identityChanged = listIdentityRef.current !== listIdentity;
        listIdentityRef.current = listIdentity;

        const requestId = ++requestIdRef.current;
        setLoading(true);

        if (identityChanged) {
            setData([]);
            setPagination(null);
        }

        if (!enabled) {
            if (requestId === requestIdRef.current) {
                setLoading(false);
                setData([]);
                setPagination(null);
            }
            return;
        }

        const commit = (fn: () => void) => {
            if (requestId === requestIdRef.current) {
                fn();
            }
        };

        try {
            const fetchFn = scope === 'CHURCH'
                ? () => fetchChurchActivityEntries(scopeId, requestParams)
                : scope === 'DISTRICT'
                    ? () => fetchDistrictActivityEntries(scopeId, requestParams)
                    : () => fetchNationActivityEntries(scopeId, requestParams);

            const response = await fetchFn();

            commit(() => {
                if (response.isSuccess()) {
                    const payload = response.getData();
                    const items = Array.isArray(payload) ? payload : payload?.data ?? [];
                    setData(items);
                    if (!Array.isArray(payload) && payload) {
                        setPagination({
                            current_page: payload.current_page,
                            last_page: payload.last_page,
                            per_page: payload.per_page,
                            total: payload.total,
                            from: payload.from ?? 0,
                            to: payload.to ?? 0,
                        });
                    } else {
                        setPagination(null);
                    }
                } else {
                    setData([]);
                    setPagination(null);
                }
            });
        } catch (err) {
            console.error("Failed to fetch activity entries:", err);
            commit(() => {
                setData([]);
                setPagination(null);
            });
        } finally {
            commit(() => {
                setLoading(false);
            });
        }
    }, [enabled, requestParams, scope, scopeId]);

    useEffect(() => {
        void load();

        return () => {
            requestIdRef.current += 1;
        };
    }, [load]);

    return { data, loading, reload: load, pagination };
}
